import React, { useState, useEffect } from 'react';
import { toast } from "react-hot-toast";
import { VscFilter } from "react-icons/vsc";
import { TbFilterX } from "react-icons/tb";

import { getBatchApi } from "../../../../Api/Batch";
import { addBatchStudentApi } from '../../../../Api/BatchStudent';
import { getCourseTypeApi } from '../../../../Api/CourseType';
import { getCourseApi } from '../../../../Api/Course';
import TimeRangeSlider from './TimeSlider';

type Props = {
    url: string;
    assignModal: boolean;
    setAssignModal: React.Dispatch<React.SetStateAction<boolean>>;
    userId: string;
    handleApiCallClick: Function;
    CourseRegistration: CourseRegistration;
}
interface CourseRegistration {
    id: string;
    courseName: string | null;
    courseType: string | null;
    courseNameId: string | null;
    courseTypeId: string | null;
    userId: string;
    duration: string;
    customDuration: string | null;
    timeZone: string;
    timeRange: string;
    status: string;
    updatedBy: string;
    updatedOn: string;
}
type Batch = {
    id: string;
    name: string;
    courseType: string;
    course: string;
    status: string;
    startTime: string;
    endTime: string;
    fees: number;
    updatedBy: string;
    updatedOn: string;
    isDeleted: boolean;
};

const AssignBatch = ({ url, assignModal, setAssignModal, userId, handleApiCallClick, CourseRegistration }: Props) => {

    const [Batch, setBatch] = useState<Batch[]>([]);
    const [CourseTypes, setCourseTypes] = useState<{ id: string; name: string }[]>([]);
    const [selectedCourseType, setSelectedCourseType] = useState<string>(CourseRegistration?.courseType);
    const [Courses, setCourses] = useState<{ id: string; name: string }[]>([]);
    const [selectedCourse, setSelectedCourse] = useState<string>(CourseRegistration?.courseName);
    const [loader, setLoader] = useState<boolean>(false);
    const [selectedBatchId, setSelectedBatchId] = useState<string | null>(null);
    const [filterModal, setFilterModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState<string>("");

    const [startTime, setStartTime] = useState("00:00");
    const [endTime, setEndTime] = useState("24:00");

    const [timeInitialized, setTimeInitialized] = useState(false);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const itemsPerPage = 10;

    useEffect(() => {
        if (CourseRegistration && !timeInitialized) {
            setSelectedCourseType(CourseRegistration.courseType);
            setSelectedCourse(CourseRegistration.courseName);
            setStartTime(CourseRegistration?.timeRange?.split("-")[0]);
            setEndTime(CourseRegistration?.timeRange?.split("-")[1]);
            setTimeInitialized(true);
        }
    }, [CourseRegistration]);

    const handleBatchApiCallClick = async () => {
        try {
            setLoader(true);
            const batchData: any = await getBatchApi(
                url,
                selectedCourseType,
                selectedCourse,
                `${startTime}-${endTime}`,
                searchTerm,
                currentPage,
                itemsPerPage
            );
            setBatch(batchData.batches || []);
            setTotalPages(Math.ceil(batchData.totalCount / itemsPerPage));

            // coursetype data
            const courseTypeData: any = await getCourseTypeApi(url);
            const courseTypeOptions = courseTypeData.map((courseType: any) => ({
                id: courseType.id,
                name: courseType.name,
            }));
            setCourseTypes(courseTypeOptions);

            // courses data
            const courseData: any = await getCourseApi(url);
            const courseOptions = courseData.map((course: any) => ({
                id: course.id,
                name: course.name,
            }));
            setCourses(courseOptions);

        } catch (error) {
            toast.error("Failed to fetch data. Some problem occurred. Please try again.");
        } finally {
            setLoader(false);
        }
    }

    async function onSubmitClick() {
        if (!selectedBatchId) {
            toast.error("Please select a batch before proceeding.");
            return;
        }
        try {
            await addBatchStudentApi(url, selectedBatchId, userId, CourseRegistration.id)
            toast.success("Batch Assigned successfully.");
            setAssignModal(false);
            handleApiCallClick();
        } catch (error) {
            toast.error("Failed to add batch student. Please try again.");
        }
    }

    useEffect(() => {
        handleBatchApiCallClick();
    }, [CourseRegistration, selectedCourseType, selectedCourse, currentPage]);

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.preventDefault();
        const value = event.target.value.toLowerCase();
        setSearchTerm(value);
    };

    const handleTimeChange = (start: string, end: string) => {
        setStartTime(start)
        setEndTime(end)
    }

    function applyFilters(event) {
        event.preventDefault();
        handleApiCallClick();
        setFilterModal(false);
    }

    function clearAllFilters() {
        setSelectedCourseType("");
        setSelectedCourse("");
        setStartTime("00:00");
        setEndTime("24:00");
        setFilterModal(false);
        handleApiCallClick();
    }

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    return (
        <div className="">

            <h1 className="text-2xl md:text-3xl font-bold text-blue-600 mb-4 md:mb-6">
                Assign Batch
            </h1>


            <div className="flex flex-row items-center w-full gap-4 justify-between md:justify-start">
                <form
                    className="w-full md:flex-grow"
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleBatchApiCallClick();
                    }}
                >
                    <label
                        htmlFor="default-search"
                        className="mb-2 text-sm font-medium text-gray-900 sr-only dark:text-white"
                    >
                        Search
                    </label>
                    <div className="relative">
                        <div className="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
                            <svg
                                className="w-4 h-4 text-gray-500 dark:text-gray-400"
                                aria-hidden="true"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 20 20"
                            >
                                <path
                                    stroke="currentColor"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"
                                />
                            </svg>
                        </div>
                        <input
                            type="search"
                            id="default-search"
                            className="w-full p-3 ps-10 outline-none text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                            placeholder="Search by Name"
                            value={searchTerm}
                            onChange={handleSearchChange}
                            required
                        />
                        <button
                            type="submit"
                            className="text-white absolute end-1.5 bottom-1.5 bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-4 py-2 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
                        >
                            Search
                        </button>
                    </div>
                </form>
                <div
                    className="flex flex-row gap-1 justify-center px-1 py-2 h-10 w-[90px] hover:bg-gray-100 rounded-md cursor-pointer"
                    onClick={() => setFilterModal(true)}
                >
                    <VscFilter className="text-blue-500 h-7 w-7" />
                    <span className="text-lg font-normal text-gray-600">Filter</span>
                </div>
            </div>

            <div className="overflow-x-auto mt-5">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 md:rounded-lg">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                        <tr>
                            <th className="px-4 py-3.5 text-md font-normal text-left text-gray-500 dark:text-gray-400">Select</th>
                            <th className="px-4 py-3.5 text-md font-normal text-left text-gray-500 dark:text-gray-400">Name</th>
                            <th className="px-4 py-3.5 text-md font-normal text-left text-gray-500 dark:text-gray-400">Course Type</th>
                            <th className="px-4 py-3.5 text-md font-normal text-left text-gray-500 dark:text-gray-400">Course</th>
                            <th className="px-4 py-3.5 text-md font-normal text-left text-gray-500 dark:text-gray-400">Start Time</th>
                            <th className="px-4 py-3.5 text-md font-normal text-left text-gray-500 dark:text-gray-400">End Time</th>
                            <th className="px-4 py-3.5 text-md font-normal text-left text-gray-500 dark:text-gray-400">Fees</th>
                            <th className="px-4 py-3.5 text-md font-normal text-left text-gray-500 dark:text-gray-400">Status</th>
                        </tr>
                    </thead>

                    <tbody className="bg-white divide-y divide-gray-200 dark:divide-gray-700 dark:bg-gray-900">
                        {loader ? (
                            <tr className="text-center">
                                <td colSpan={12} className="py-3 text-center">
                                    <div className="w-12 h-12 border-4 text-center border-t-blue-500 border-gray-300 rounded-full animate-spin mx-auto"></div>
                                </td>
                            </tr>
                        ) : (
                            Batch.map((item) => (
                                <tr key={item.id} className="hover:bg-gray-50">
                                    <td className="px-4 py-4 text-sm whitespace-nowrap">
                                        <input
                                            type="radio"
                                            name="batch"
                                            value={item.id}
                                            onChange={() => setSelectedBatchId(item.id)} // Update selected batch ID
                                        />
                                    </td>
                                    <td className="px-4 py-4 text-sm font-normal text-gray-700 whitespace-nowrap">{item.name}</td>
                                    <td className="px-4 py-4 text-sm font-normal text-gray-700 whitespace-nowrap">{item.courseType}</td>
                                    <td className="px-4 py-4 text-sm font-normal text-gray-700 whitespace-nowrap">{item.course}</td>
                                    <td className="px-4 py-4 text-sm font-normal text-gray-700 whitespace-nowrap">{item.startTime}</td>
                                    <td className="px-4 py-4 text-sm font-normal text-gray-700 whitespace-nowrap">{item.endTime}</td>
                                    <td className="px-4 py-4 text-sm font-normal text-gray-700 whitespace-nowrap">{item.fees}</td>
                                    <td className="px-4 py-4 text-sm font-normal text-gray-700 whitespace-nowrap">{item.status}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            <div className="flex flex-wrap items-center justify-center md:justify-between mt-6 gap-2 md:gap-4">
                <button
                    disabled={currentPage === 1}
                    onClick={handlePreviousPage}
                    className="flex items-center px-5 py-2 text-sm text-gray-700 capitalize transition-colors duration-200 bg-white border rounded-md gap-x-2 hover:bg-gray-100 dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700 dark:hover:bg-gray-800"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="1.5"
                        stroke="currentColor"
                        className="w-5 h-5 rtl:-scale-x-100"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M6.75 15.75L3 12m0 0l3.75-3.75M3 12h18"
                        />
                    </svg>
                    <span>Previous</span>
                </button>
                <div className="flex flex-wrap items-center justify-center gap-x-2">
                    {[...Array(Math.max(totalPages, 1))].map((_, index) => (
                        <button
                            key={index + 1}
                            onClick={() => setCurrentPage(index + 1)}
                            className={`px-2 py-1 text-sm ${currentPage === index + 1
                                ? "text-blue-500 bg-blue-100"
                                : "text-gray-500 hover:bg-gray-100"
                                } rounded-md`}
                        >
                            {index + 1}
                        </button>
                    ))}
                </div>
                <button
                    disabled={currentPage === totalPages}
                    onClick={handleNextPage}
                    className="flex items-center px-5 py-2 text-sm text-gray-700 capitalize transition-colors duration-200 bg-white border rounded-md gap-x-2 hover:bg-gray-100 dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700 dark:hover:bg-gray-800"
                >
                    <span>Next</span>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="1.5"
                        stroke="currentColor"
                        className="w-5 h-5 rtl:-scale-x-100"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3"
                        />
                    </svg>
                </button>
            </div>

            <div className=" flex gap-2 md:gap-4 mt-5">
                <button
                    type='button'
                    className="flex items-center justify-center w-fit px-4 py-2 mt-3 font-medium tracking-wide text-white capitalize transition-colors duration-300 transform bg-blue-600 rounded-lg hover:bg-blue-500 focus:outline-none focus:ring focus:ring-blue-300 focus:ring-opacity-80"
                    onClick={onSubmitClick}
                >
                    Assign Batch
                </button>

                <button
                    className="flex items-center justify-center w-fit px-4 py-2 mt-3 font-medium tracking-wide capitalize transition-colors duration-300 transform border border-gray-800  rounded-md hover:bg-gray-200"
                    onClick={() => setAssignModal(false)}
                >
                    Cancel
                </button>
            </div>

            {/* filter model */}
            {filterModal && (
                <div className="inset-0 bg-gray-800 bg-opacity-50 z-50 transition-opacity duration-300">
                    <div
                        className={`fixed top-0 right-0 h-full w-full lg:w-[500px] md:w-[500px] overflow-y-auto bg-white p-4 rounded-md shadow-lg transform transition-transform duration-500 ease-in-out `}
                    >
                        <h1 className="text-2xl md:text-3xl font-bold text-blue-600 mb-4 md:mb-6">
                            Filter
                        </h1>
                        <button
                            onClick={() => setFilterModal(false)}
                            className="absolute top-1 right-3 text-gray-400 hover:text-gray-600 text-2xl"
                        >
                            &times;
                        </button>

                        <div className="  flex items-center my-4">
                            <div
                                onClick={clearAllFilters}
                                className=" cursor-pointer flex items-center gap-2 hover:bg-gray-200 py-1 rounded-sm"
                            >
                                <TbFilterX className="text-blue-500 h-5 w-5" />
                                <span className="text-sm">Clear all</span>
                            </div>
                        </div>

                        <div className="flex flex-col gap-3">


                            <div className="flex flex-col">
                                <div className="mb-4 flex flex-col">
                                    <label className="text-base font-semibold text-gray-900 mb-2">Course Type</label>
                                    <div className="w-full">
                                        <select
                                            value={selectedCourseType}
                                            onChange={(e) => setSelectedCourseType(e.target.value)}
                                            required
                                            className="w-[96%] px-4 py-2 text-gray-800 bg-white border border-gray-400 rounded-md dark:bg-gray-800 dark:text-gray-300 dark:border-gray-800 focus:border-blue-400 focus:ring-blue-300 focus:ring-opacity-40 dark:focus:border-blue-300 focus:outline-none focus:ring"
                                        >
                                            <option value="">All</option>
                                            {CourseTypes.map((courseType: { id: string; name: string }) => (
                                                <option key={courseType.id} value={courseType.name}>
                                                    {courseType.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div className="flex flex-col">
                                <div className="mb-4 flex flex-col">
                                    <label className="text-base font-semibold text-gray-900 mb-2">Course</label>
                                    <div className="w-full">
                                        <select
                                            value={selectedCourse}
                                            onChange={(e) => setSelectedCourse(e.target.value)}
                                            required
                                            className="w-[96%] px-4 py-2 text-gray-800 bg-white border border-gray-400 rounded-md dark:bg-gray-800 dark:text-gray-300 dark:border-gray-800 focus:border-blue-400 focus:ring-blue-300 focus:ring-opacity-40 dark:focus:border-blue-300 focus:outline-none focus:ring"
                                        >
                                            <option value="">All</option>
                                            {Courses.map((course: { id: string; name: string }) => (
                                                <option key={course.id} value={course.name}>
                                                    {course.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div className="mb-4 flex flex-col">
                                <label className="text-base font-semibold text-gray-900 mb-2">Time range</label>
                                <div className="rounded-md w-[96%] bg-white border border-gray-400 p-6 mb-4 shadow-sm">
                                    <TimeRangeSlider startTime={startTime} endTime={endTime} onChange={handleTimeChange} />
                                </div>
                            </div>

                        </div>

                        <div className=" flex gap-2 md:gap-4 mt-5">
                            <button
                                onClick={applyFilters}
                                className="mt-2 px-4 py-1 text-white border border-gray-800 rounded-sm bg-blue-600 hover:bg-blue-700"
                            >
                                Apply
                            </button>
                            <button
                                className="mt-2 px-4 py-1 border border-gray-800  rounded-sm hover:bg-gray-200"
                                onClick={() => setFilterModal(false)}
                            >
                                Cancel
                            </button>
                        </div>

                    </div>
                </div>
            )}
        </div>
    )
}

export default AssignBatch